import { useState, useEffect } from 'react';
import ErrorHandler from '@Root/ErrorHandler';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import ProjectService from '@Helpers/URLHelpers/ProjectService';
import InvitationService from '@Helpers/URLHelpers/InvitationService';
import TeamService from '@Helpers/URLHelpers/AuthService/TeamService';
import { SnackbarManager } from '@CommonFunctionalDisplay/GlobalSnackbar';
import BorderBlueButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderBlueButtonV2';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { ReactComponent as Search } from '@Assets/search.svg';
import { MdPeople as People, MdGroups as Groups, MdExpandMore, MdExpandLess, MdPersonAdd } from 'react-icons/md';
import CircularProgress from '@mui/material/CircularProgress';
import SendInvitationModal from './SendInvitationModal';
import CreateTeamModal from './CreateTeamModal';
import EditUserPermissionsModal from './EditUserPermissionsModal';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_ComStyles.scss';
import './CollaboratorsPage.scss';

export default function CollaboratorsPage(props) {
    const [projectTeams, setProjectTeams] = useState([]);
    const [projectUsers, setProjectUsers] = useState([]);
    const [pendingInvitations, setPendingInvitations] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('members');
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [confirmingRemoveId, setConfirmingRemoveId] = useState(null);
    const [confirmingRevokeId, setConfirmingRevokeId] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [expandedTeamIds, setExpandedTeamIds] = useState(new Set());
    const [teamMembersMap, setTeamMembersMap] = useState({});
    const [teamMembersLoading, setTeamMembersLoading] = useState(new Set());
    const [confirmingTeamRemoveKey, setConfirmingTeamRemoveKey] = useState(null);
    const [inviteTeamId, setInviteTeamId] = useState(null);
    const [inviteTeamName, setInviteTeamName] = useState(null);
    const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
    const [permissionsUser, setPermissionsUser] = useState(null);

    const projectService = new ProjectService();
    const invitationService = new InvitationService();
    const teamService = new TeamService();
    const errorHandler = new ErrorHandler();
    const { currentProjectId, currentUserProjectPermissions } = useReduxProject();
    const { currentOrgId } = useReduxOrg();
    const cn = 'project__collaboratorsPage';

    useEffect(() => {
        renderData();
    }, []);

    const renderData = async () => {
        try {
            setLoading(true);
            await Promise.all([fetchMembers(), fetchPendingInvitations()]);
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchMembers = async () => {
        const [teamResponse, userResponse] = await Promise.all([
            projectService.getTeamPermissionListForProject(currentProjectId),
            projectService.getUserPermissionListForProject(currentProjectId),
        ]);
        const teamList = JSON.parse(JSON.parse(teamResponse.body).body);
        const userList = JSON.parse(JSON.parse(userResponse.body).body);
        setProjectTeams(teamList);
        setProjectUsers(userList);
    };

    const fetchPendingInvitations = async () => {
        try {
            const res = await invitationService.getInvitationListForOrg(currentOrgId);
            const list = res?.body ? JSON.parse(res.body) : (Array.isArray(res) ? res : []);
            setPendingInvitations(list);
        } catch (_) {
            // Non-critical — silently skip
        }
    };

    const handleInvitationSent = async () => {
        await renderData();
    };

    const handleRemoveUser = async (user) => {
        try {
            setActionLoading(true);
            await projectService.removeContributorPermissionFromProject({
                projectId: Number(currentProjectId),
                userId: user.userId ?? user.id,
            });
            SnackbarManager.show?.('Member removed', 'success');
            setConfirmingRemoveId(null);
            await fetchMembers();
        } catch (err) {
            errorHandler.handleError(err);
            SnackbarManager.show?.('Failed to remove member', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRevokeInvitation = async (inv) => {
        try {
            setActionLoading(true);
            const res = await invitationService.deleteInvitation({
                action: 'delete',
                invitationId: Number(inv.id),
                orgId: Number(currentOrgId),
            });
            const parsed = res?.body ? JSON.parse(res.body) : res;
            if (parsed?.message !== 'Invitation Deleted') {
                throw new Error(parsed?.message || 'Unexpected response');
            }
            SnackbarManager.show?.('Invitation revoked', 'success');
            setConfirmingRevokeId(null);
            await fetchPendingInvitations();
        } catch (err) {
            errorHandler.handleError(err);
            SnackbarManager.show?.('Failed to revoke invitation', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const fetchTeamMembers = async (teamId) => {
        setTeamMembersLoading(prev => new Set([...prev, teamId]));
        try {
            const res = await teamService.getUserListByTeamId(teamId);
            const members = res?.body ? JSON.parse(res.body) : (Array.isArray(res) ? res : []);
            setTeamMembersMap(prev => ({ ...prev, [teamId]: members }));
        } catch (_) {
            setTeamMembersMap(prev => ({ ...prev, [teamId]: [] }));
        } finally {
            setTeamMembersLoading(prev => {
                const next = new Set(prev);
                next.delete(teamId);
                return next;
            });
        }
    };

    const toggleTeamExpand = (teamId) => {
        setExpandedTeamIds(prev => {
            const next = new Set(prev);
            if (next.has(teamId)) {
                next.delete(teamId);
            } else {
                next.add(teamId);
                if (teamMembersMap[teamId] === undefined) {
                    fetchTeamMembers(teamId);
                }
            }
            return next;
        });
    };

    const handleRemoveTeamMember = async (member, teamId) => {
        try {
            setActionLoading(true);
            await teamService.removeContributorPermissionFromTeam({
                childType: 'user',
                userId: member.userId ?? member.id,
                teamId: Number(teamId),
            });
            SnackbarManager.show?.('Member removed from team', 'success');
            setConfirmingTeamRemoveKey(null);
            await fetchTeamMembers(teamId);
        } catch (err) {
            errorHandler.handleError(err);
            SnackbarManager.show?.('Failed to remove member', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const openTeamInvite = (teamId, teamName) => {
        setInviteTeamId(teamId);
        setInviteTeamName(teamName);
        setShowInviteModal(true);
    };

    const switchTab = (tab) => {
        setActiveTab(tab);
        setSearchQuery('');
        setConfirmingRemoveId(null);
        setConfirmingRevokeId(null);
        setConfirmingTeamRemoveKey(null);
    };

    const q = searchQuery.toLowerCase();
    const filteredUsers = projectUsers.filter(u =>
        u.userName?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.fullName?.toLowerCase().includes(q)
    );
    const filteredTeams = projectTeams.filter(t =>
        t.teamName?.toLowerCase().includes(q)
    );
    const filteredInvitations = pendingInvitations.filter(inv =>
        inv.inviteeEmail?.toLowerCase().includes(q) ||
        inv.inviteeFullName?.toLowerCase().includes(q) ||
        inv.inviteeUserName?.toLowerCase().includes(q)
    );

    const existingUserIds = new Set(
        projectUsers.map(u => u.userId ?? u.id).filter(Boolean).map(id => Number(id))
    );

    // inviteeId is "0" for email-only invitations — exclude those
    const pendingInviteeIds = new Set(
        pendingInvitations
            .map(inv => inv.inviteeId)
            .filter(id => id && Number(id) !== 0)
            .map(id => Number(id))
    );

    const pendingInviteeEmails = new Set(
        pendingInvitations.map(inv => inv.inviteeEmail?.toLowerCase()).filter(Boolean)
    );

    const permissionChips = (entity) => {
        const chips = [];
        if (entity?.allowAdmin) chips.push('Admin');
        if (entity?.allowEdit) chips.push('Edit');
        if (entity?.allowView) chips.push('View');
        if (entity?.allowDelete) chips.push('Delete');
        if (chips.length === 0) chips.push('Member');
        return chips;
    };

    const isAdmin = currentUserProjectPermissions?.allowAdmin;

    const tabs = [
        { key: 'members', label: 'Members', count: projectUsers.length },
        { key: 'teams',   label: 'Teams',   count: projectTeams.length },
        { key: 'pending', label: 'Pending', count: pendingInvitations.length },
    ];

    const InlineConfirm = ({ label, onConfirm, onCancel }) => (
        <div className={`${cn}__row__confirm`}>
            <span className={`${cn}__row__confirmText`}>{label}</span>
            <button
                className={`${cn}__row__confirmBtn ${cn}__row__confirmBtn--danger`}
                onClick={onConfirm}
                disabled={actionLoading}
            >
                {actionLoading ? <CircularProgress size={11} color="inherit" /> : 'Yes'}
            </button>
            <button
                className={`${cn}__row__confirmBtn`}
                onClick={onCancel}
                disabled={actionLoading}
            >
                No
            </button>
        </div>
    );

    const EmptyState = ({ icon, title, text }) => (
        <div className={`${cn}__empty`}>
            <div className={`${cn}__empty__icon`}>{icon}</div>
            <div className={`${cn}__empty__title`}>{title}</div>
            <div className={`${cn}__empty__text`}>{text}</div>
        </div>
    );

    return (
        <div className={cn}>
            <div className={`${cn}__container`}>

                {/* Header */}
                <div className={`${cn}__header`}>
                    <div className={`${cn}__header__left`}>
                        <h1 className={`${cn}__header__title`}>Teams and Users</h1>
                        <div className={`${cn}__header__stats`}>
                            <span>{projectUsers.length} member{projectUsers.length !== 1 ? 's' : ''}</span>
                            <span className={`${cn}__header__dot`}>·</span>
                            <span>{projectTeams.length} team{projectTeams.length !== 1 ? 's' : ''}</span>
                            {pendingInvitations.length > 0 && (
                                <>
                                    <span className={`${cn}__header__dot`}>·</span>
                                    <span
                                        className={`${cn}__header__pendingLink`}
                                        onClick={() => switchTab('pending')}
                                    >
                                        {pendingInvitations.length} pending
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                    {isAdmin && (
                        <div className={`${cn}__headerActions`}>
                            {activeTab === 'teams' && (
                                <BorderBlueButtonV2
                                    onClick={() => setShowCreateTeamModal(true)}
                                    text="New Team"
                                />
                            )}
                            <BorderBlueButtonV2
                                onClick={() => { setInviteTeamId(null); setInviteTeamName(null); setShowInviteModal(true); }}
                                text="Invite Collaborators"
                            />
                        </div>
                    )}
                </div>

                {/* Toolbar */}
                <div className={`${cn}__toolbar`}>
                    <div className={`${cn}__tabs`}>
                        {tabs.map(tab => (
                            <button
                                key={tab.key}
                                className={`${cn}__tab ${activeTab === tab.key ? 'active' : ''}`}
                                onClick={() => switchTab(tab.key)}
                            >
                                {tab.label}
                                <span className={`${cn}__tab__badge ${tab.key === 'pending' && tab.count > 0 ? 'amber' : ''} ${activeTab === tab.key ? 'active' : ''}`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>
                    <TextField
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        variant="outlined"
                        size="small"
                        className={`${cn}__search`}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search className={`${cn}__searchIcon`} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </div>

                {/* Content panel */}
                <div className={`${cn}__panel`}>
                    {loading ? (
                        <div className={`${cn}__loading`}>
                            <CircularProgress size={24} />
                            <span>Loading collaborators...</span>
                        </div>
                    ) : (
                        <>
                            {/* Members */}
                            {activeTab === 'members' && (
                                filteredUsers.length === 0
                                    ? <EmptyState
                                        icon={<People />}
                                        title={searchQuery ? 'No members found' : 'No members yet'}
                                        text={searchQuery ? 'Try a different search term' : 'Invite users to start collaborating on this project'}
                                      />
                                    : filteredUsers.map(user => {
                                        const userId = user.userId ?? user.id;
                                        const isConfirming = confirmingRemoveId === userId;
                                        return (
                                            <div
                                                key={userId ?? user.userName}
                                                className={`${cn}__row ${cn}__row--clickable`}
                                                onClick={() => setPermissionsUser(user)}
                                            >
                                                <div className={`${cn}__row__avatar`}>
                                                    {(user.fullName || user.userName || 'U').slice(0, 1).toUpperCase()}
                                                </div>
                                                <div className={`${cn}__row__info`}>
                                                    <div className={`${cn}__row__name`}>
                                                        {user.fullName || user.userName || 'Unknown user'}
                                                    </div>
                                                    <div className={`${cn}__row__sub`}>
                                                        {user.userName
                                                            ? `@${user.userName}`
                                                            : (user.userEmail || user.email || '—')}
                                                    </div>
                                                </div>
                                                <div className={`${cn}__row__chips`}>
                                                    {permissionChips(user).map(chip => (
                                                        <span key={chip} className={`${cn}__chip ${cn}__chip--${chip.toLowerCase()}`}>
                                                            {chip}
                                                        </span>
                                                    ))}
                                                </div>
                                                {isAdmin && (
                                                    <div className={`${cn}__row__actions`} onClick={(e) => e.stopPropagation()}>
                                                        {isConfirming
                                                            ? <InlineConfirm
                                                                label="Remove member?"
                                                                onConfirm={() => handleRemoveUser(user)}
                                                                onCancel={() => setConfirmingRemoveId(null)}
                                                              />
                                                            : <button
                                                                className={`${cn}__row__actionBtn`}
                                                                onClick={() => setConfirmingRemoveId(userId)}
                                                              >
                                                                Remove
                                                              </button>
                                                        }
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                            )}

                            {/* Teams */}
                            {activeTab === 'teams' && (
                                filteredTeams.length === 0
                                    ? <EmptyState
                                        icon={<Groups />}
                                        title={searchQuery ? 'No teams found' : 'No teams added'}
                                        text={searchQuery ? 'Try a different search term' : 'No teams have been added to this project'}
                                      />
                                    : filteredTeams.map(team => {
                                        const teamId = team.teamId ?? team.id;
                                        const isExpanded = expandedTeamIds.has(teamId);
                                        const isMembersLoading = teamMembersLoading.has(teamId);
                                        const members = teamMembersMap[teamId] || [];
                                        return (
                                            <div key={teamId ?? team.teamName} className={`${cn}__teamCard`}>
                                                <div
                                                    className={`${cn}__teamCard__header`}
                                                    onClick={() => toggleTeamExpand(teamId)}
                                                >
                                                    <div className={`${cn}__row__avatar ${cn}__row__avatar--team`}>
                                                        <Groups />
                                                    </div>
                                                    <div className={`${cn}__teamCard__info`}>
                                                        <div className={`${cn}__teamCard__name`}>{team.teamName || 'Unnamed team'}</div>
                                                        <div className={`${cn}__teamCard__tag`}>{team.tag || team.teamTag || 'Team'}</div>
                                                    </div>
                                                    <div className={`${cn}__row__chips`}>
                                                        {permissionChips(team).map(chip => (
                                                            <span key={chip} className={`${cn}__chip ${cn}__chip--${chip.toLowerCase()}`}>
                                                                {chip}
                                                            </span>
                                                        ))}
                                                    </div>
                                                    <div className={`${cn}__teamCard__controls`}>
                                                        {isAdmin && (
                                                            <button
                                                                className={`${cn}__teamCard__inviteBtn`}
                                                                onClick={(e) => { e.stopPropagation(); openTeamInvite(teamId, team.teamName); }}
                                                            >
                                                                <MdPersonAdd />
                                                                Invite
                                                            </button>
                                                        )}
                                                        <span className={`${cn}__teamCard__chevron`}>
                                                            {isExpanded ? <MdExpandLess /> : <MdExpandMore />}
                                                        </span>
                                                    </div>
                                                </div>
                                                {isExpanded && (
                                                    <div className={`${cn}__teamCard__body`}>
                                                        {isMembersLoading ? (
                                                            <div className={`${cn}__teamCard__membersLoading`}>
                                                                <CircularProgress size={14} />
                                                                <span>Loading members...</span>
                                                            </div>
                                                        ) : members.length === 0 ? (
                                                            <div className={`${cn}__teamCard__membersEmpty`}>
                                                                No members in this team yet — use Invite to add people
                                                            </div>
                                                        ) : (
                                                            members.map(member => {
                                                                const memberKey = `${teamId}-${member.userId ?? member.id}`;
                                                                const isConfirming = confirmingTeamRemoveKey === memberKey;
                                                                return (
                                                                    <div key={memberKey} className={`${cn}__teamCard__memberRow`}>
                                                                        <div className={`${cn}__row__avatar`}>
                                                                            {(member.fullName || member.userName || 'U').slice(0, 1).toUpperCase()}
                                                                        </div>
                                                                        <div className={`${cn}__row__info`}>
                                                                            <div className={`${cn}__row__name`}>{member.fullName || member.userName || 'Unknown'}</div>
                                                                            <div className={`${cn}__row__sub`}>{member.userName ? `@${member.userName}` : (member.userEmail || member.email || '—')}</div>
                                                                        </div>
                                                                        {isAdmin && (
                                                                            <div className={`${cn}__row__actions`}>
                                                                                {isConfirming
                                                                                    ? <InlineConfirm
                                                                                        label="Remove from team?"
                                                                                        onConfirm={() => handleRemoveTeamMember(member, teamId)}
                                                                                        onCancel={() => setConfirmingTeamRemoveKey(null)}
                                                                                      />
                                                                                    : <button
                                                                                        className={`${cn}__row__actionBtn`}
                                                                                        style={{ opacity: 1 }}
                                                                                        onClick={() => setConfirmingTeamRemoveKey(memberKey)}
                                                                                      >
                                                                                        Remove
                                                                                      </button>
                                                                                }
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                            )}

                            {/* Pending invitations */}
                            {activeTab === 'pending' && (
                                filteredInvitations.length === 0
                                    ? <EmptyState
                                        icon="✉"
                                        title="No pending invitations"
                                        text="All sent invitations have been accepted, or none have been sent yet"
                                      />
                                    : filteredInvitations.map(inv => {
                                        const isConfirming = confirmingRevokeId === inv.id;
                                        const hasName = inv.inviteeFullName && inv.inviteeFullName !== 'N/A';
                                        const hasUsername = inv.inviteeUserName && inv.inviteeUserName !== 'N/A';
                                        const label = hasName ? inv.inviteeFullName : inv.inviteeEmail;
                                        const subText = hasName
                                            ? (hasUsername ? `@${inv.inviteeUserName} · ${inv.inviteeEmail}` : inv.inviteeEmail)
                                            : 'Email invitation';
                                        const sentDate = inv.createdAt
                                            ? new Date(inv.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                                            : null;
                                        return (
                                            <div key={inv.id} className={`${cn}__row`}>
                                                <div className={`${cn}__row__avatar ${cn}__row__avatar--email`}>✉</div>
                                                <div className={`${cn}__row__info`}>
                                                    <div className={`${cn}__row__name`}>{label}</div>
                                                    <div className={`${cn}__row__sub`}>
                                                        {subText}{sentDate ? ` · Invited ${sentDate}` : ''}
                                                    </div>
                                                </div>
                                                <div className={`${cn}__row__chips`}>
                                                    <span className={`${cn}__chip ${cn}__chip--pending`}>Pending</span>
                                                </div>
                                                {isAdmin && (
                                                    <div className={`${cn}__row__actions`}>
                                                        {isConfirming
                                                            ? <InlineConfirm
                                                                label="Revoke invitation?"
                                                                onConfirm={() => handleRevokeInvitation(inv)}
                                                                onCancel={() => setConfirmingRevokeId(null)}
                                                              />
                                                            : <button
                                                                className={`${cn}__row__actionBtn`}
                                                                onClick={() => setConfirmingRevokeId(inv.id)}
                                                              >
                                                                Revoke
                                                              </button>
                                                        }
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                            )}
                        </>
                    )}
                </div>
            </div>

            <SendInvitationModal
                open={showInviteModal}
                onClose={() => { setShowInviteModal(false); setInviteTeamId(null); setInviteTeamName(null); }}
                existingUserIds={existingUserIds}
                pendingInviteeIds={pendingInviteeIds}
                pendingInviteeEmails={pendingInviteeEmails}
                onInvitationSent={handleInvitationSent}
                inviteTeamId={inviteTeamId}
                inviteTeamName={inviteTeamName}
            />

            <CreateTeamModal
                open={showCreateTeamModal}
                onClose={() => setShowCreateTeamModal(false)}
                existingTeams={projectTeams}
                existingUserIds={existingUserIds}
                onTeamCreated={fetchMembers}
            />

            <EditUserPermissionsModal
                open={!!permissionsUser}
                onClose={() => setPermissionsUser(null)}
                user={permissionsUser}
                projectId={currentProjectId}
                isAdmin={isAdmin}
                onSaved={fetchMembers}
            />
        </div>
    );
}
